import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/auth.service';
import { sessionService } from '../services/session.service';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  provider: 'google' | 'linkedin' | 'apple';
  resume?: {
    url: string;
    path?: string;
    uploadedAt: string;
  };
  salaryRange?: {
    min: number;
    max: number;
  };
  skills?: string[];
  companyId?: string;
  companyName?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
  login: (provider: 'google' | 'linkedin' | 'apple') => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize session from cookies
    const session = sessionService.initializeSession();
    if (session) {
      // Session exists, check if still valid
      checkAuth();
    } else {
      setLoading(false);
    }

    // Listen for OAuth login events
    const handleAuthLogin = (event: CustomEvent<UserProfile>) => {
      setUser(event.detail);
      setIsAuthenticated(true);
      setLoading(false);
      
      // Create session with the new auth data
      const token = localStorage.getItem('jobometry_auth_token');
      if (token) {
        sessionService.createSession(token, {
          userId: event.detail.id,
          email: event.detail.email,
          name: event.detail.name
        });
      }
    };

    window.addEventListener('auth:login', handleAuthLogin as EventListener);

    return () => {
      window.removeEventListener('auth:login', handleAuthLogin as EventListener);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await authService.getMe();
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // User not authenticated
      console.log('User not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const login = async (provider: 'google' | 'linkedin' | 'apple') => {
    try {
      setLoading(true);
      await authService.login(provider);
      // The OAuth flow will redirect, so we don't need to handle the response here
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      sessionService.clearSession(); // Clear session cookie
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      const updatedUser = await authService.updateProfile(updates);
      setUser(updatedUser);
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};